import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

// Convert DynamoDB wire format ({S,N,L,M,BOOL,NULL}) to plain JSON. Mirrors
// IntakeEHRPayloadMapper.unmarshal but lives here so we don't pull server
// modules into the client bundle.
function unmarshalDynamo(item) {
  if (item === null || item === undefined) return item;
  if (Array.isArray(item)) return item.map(unmarshalDynamo);
  if (typeof item !== 'object') return item;
  if (item.S !== undefined) return item.S;
  if (item.N !== undefined) return Number(item.N);
  if (item.BOOL !== undefined) return item.BOOL;
  if (item.NULL === true) return null;
  if (item.L) return item.L.map(unmarshalDynamo);
  if (item.M) {
    const out = {};
    for (const [k, v] of Object.entries(item.M)) out[k] = unmarshalDynamo(v);
    return out;
  }
  // Plain object (already unmarshalled or partial) — recurse
  const out = {};
  for (const [k, v] of Object.entries(item)) out[k] = unmarshalDynamo(v);
  return out;
}

export default function GenerationLog({ practiceId }) {
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState('');
  const [previewType, setPreviewType] = useState('');
  const [previewJSON, setPreviewJSON] = useState(null);
  const [apptTypes, setApptTypes] = useState([]);
  const [toast, setToast] = useState(null);
  const [copied, setCopied] = useState(false);

  const showToast = (msg, kind = 'success') => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 3000);
  };

  const load = useCallback(async () => {
    try {
      const [logsRes, atRes] = await Promise.all([
        api.getIntakeGenerationLogs(practiceId, { push_status: filter || undefined, limit: 50 }),
        api.getEHRAppointmentTypes(practiceId),
      ]);
      setLogs(logsRes.data || []);
      setApptTypes(atRes.data || []);
    } catch (err) { console.error(err); }
  }, [practiceId, filter]);

  useEffect(() => { load(); }, [load]);

  const regenerate = async (id) => {
    if (!window.confirm('Regenerate intake form? Patient answers will be preserved if form is in progress.')) return;
    try {
      await api.regenerateIntakeForm(practiceId, id);
      showToast('Form regenerated. The patient will get a fresh link if a delivery channel is configured.', 'success');
      load();
    } catch (err) {
      showToast(`Regenerate failed: ${err.message}`, 'error');
    }
  };

  const preview = async () => {
    if (!previewType) return;
    try {
      const res = await api.previewIntakeJSON(practiceId, previewType);
      // Unmarshal DynamoDB wire format → plain JSON for display
      setPreviewJSON(unmarshalDynamo(res.data));
    } catch (err) { showToast(`Preview failed: ${err.message}`, 'error'); }
  };

  const copyPreviewJson = async () => {
    if (!previewJSON) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(previewJSON, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) { showToast(`Copy failed: ${err.message}`, 'error'); }
  };

  return (
    <div style={s.section}>
      <div style={s.sectionHeader}>
        <h3 style={s.sectionTitle}>Generation Log</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select style={s.input} value={filter} onChange={e => setFilter(e.target.value)}>
            <option value="">All Statuses</option>
            <option value="PUSHED">Pushed</option>
            <option value="PENDING">Pending</option>
            <option value="FAILED">Failed</option>
          </select>
        </div>
      </div>

      {/* Preview Tool */}
      <div style={{ ...s.formCard, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select style={{ ...s.input, flex: 1 }} value={previewType} onChange={e => setPreviewType(e.target.value)}>
            <option value="">Select appointment type...</option>
            {apptTypes.map(at => (
              // GH-98/99: id may be null when sourced from appointments or
              // intake_appointment_type_mappings rather than the canonical
              // ehr_appointment_types row. Fall back to name.
              <option key={at.id ?? at.name} value={at.name}>{at.name}{at.duration_minutes ? ` (${at.duration_minutes} min)` : ''}</option>
            ))}
          </select>
          <button style={s.btnPrimary} onClick={preview}>Preview JSON</button>
        </div>
        {previewJSON && (
          <div style={{ marginTop: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
              <button style={s.btnSmall} onClick={copyPreviewJson}>
                {copied ? 'Copied!' : 'Copy JSON'}
              </button>
            </div>
            <pre style={{ background: '#1e293b', color: '#e2e8f0', padding: 16, borderRadius: 8, fontSize: 11, maxHeight: 400, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(previewJSON, null, 2)}
            </pre>
          </div>
        )}
      </div>

      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Patient</th>
            <th style={s.th}>Appt Type</th>
            <th style={s.th}>Package</th>
            <th style={s.th}>Status</th>
            <th style={s.th}>Pushed At</th>
            <th style={s.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {logs.map(log => (
            <tr key={log.id}>
              <td style={s.td}>{log.patient_name || `Patient #${log.patient_id}`}</td>
              <td style={s.td}>{log.appointment_type || '—'}</td>
              <td style={s.td}>{log.package_name || '—'}</td>
              <td style={s.td}>
                <span style={{
                  ...s.badge,
                  background: log.push_status === 'PUSHED' ? '#d1fae5' : log.push_status === 'FAILED' ? '#fee2e2' : '#fef3c7',
                  color: log.push_status === 'PUSHED' ? '#065f46' : log.push_status === 'FAILED' ? '#991b1b' : '#92400e'
                }}>
                  {log.push_status}
                </span>
              </td>
              <td style={s.td}>{log.pushed_at ? new Date(log.pushed_at).toLocaleString() : '—'}</td>
              <td style={s.td}>
                <button style={s.btnSmall} onClick={() => regenerate(log.id)}>Regenerate</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {logs.length === 0 && <p style={s.empty}>No generation logs yet.</p>}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, padding: '10px 16px',
          borderRadius: 8, fontSize: 13, fontWeight: 500, color: '#fff', zIndex: 1000,
          background: toast.kind === 'error' ? '#dc2626' : '#16a34a',
          boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
        }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}

const s = {
  section: { marginTop: 8 },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  sectionTitle: { fontSize: 16, fontWeight: 600, color: '#111827', margin: 0 },
  formCard: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 12 },
  input: { padding: '6px 10px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 6, outline: 'none' },
  btnPrimary: { padding: '6px 14px', fontSize: 13, background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 500 },
  btnSmall: { padding: '3px 8px', fontSize: 12, background: 'none', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', color: '#4f46e5' },
  badge: { display: 'inline-block', fontSize: 11, padding: '2px 6px', borderRadius: 4, fontWeight: 500 },
  empty: { textAlign: 'center', color: '#9ca3af', fontSize: 13, padding: 20 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '8px 12px', borderBottom: '2px solid #e5e7eb', fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' },
  td: { padding: '8px 12px', borderBottom: '1px solid #f3f4f6' },
};
